import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { FeedbackButton } from "../components/FeedbackButton";
import { useLocation } from "react-router-dom";

import { CardTitle } from "../components/CardTitle";
import { HomeHand } from "../components/HomeHand";
import { MenuIcon } from "../components/MenuIcon";
import { PrivacyModal } from "../components/PrivacyModal";
import { RulesModal } from "../components/RulesModal";
import { SettingsModal } from "../components/SettingsModal";
import { useAccountStore } from "../../account/accountStore";
import { hasResumableRound, hasSeenTutorial, loadLocalConfig, loadQuickPlay, quickPlayTable } from "../../state/persistence";
import { toGameConfig, useGameStore } from "../../state/gameStore";
import { privacyLinkVisible } from "../../legal/privacy";
import { levelProgress } from "../../stats/levels";
import { loadProfile, statTotal } from "../../stats/profile";
import { shouldOfferTutorial } from "../../tutorial/firstRun";
import { TUTORIALS } from "../../tutorial/tutorials";
import { versionLabel } from "../../version";
import { useTransitionNavigate } from "../transitions/useTransitionNavigate";
import { campaignVisible } from "../../campaign/release";

/**
 * Code-split for the reason App.tsx splits every route past this one: the whole
 * of CHANGELOG.md is inside it, and that is ~10kB of text on the first paint of
 * the home screen for a sheet most players will never open. It arrives when the
 * version number is tapped.
 */
const WhatsNewModal = lazy(() => import("../components/WhatsNewModal").then((m) => ({ default: m.WhatsNewModal })));

export function HomeScreen() {
  const nav = useTransitionNavigate();
  // Set by a screen that sent the player here with something to say - deleting
  // their account - the same way Play Online is told a game vanished.
  const notice = (useLocation().state as { notice?: string } | null)?.notice ?? null;
  const [showRules, setShowRules] = useState(false);
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [howToOpen, setHowToOpen] = useState(false);
  const resumeSavedRound = useGameStore((s) => s.resumeSavedRound);
  const startGame = useGameStore((s) => s.startGame);
  // Computed once on mount: whether a refresh left a round mid-play.
  const [canResume] = useState(() => hasResumableRound());
  const accountStatus = useAccountStore((s) => s.status);
  const username = useAccountStore((s) => s.username);
  const profile = loadProfile();
  const progress = levelProgress(profile);
  const deckConfig = loadLocalConfig().deckConfig;
  const closeSettings = useCallback(() => setShowSettings(false), []);
  const closePrivacy = useCallback(() => setShowPrivacy(false), []);
  // From Settings, the policy REPLACES that sheet rather than opening over it:
  // Settings sits on `.confirm-overlay`, which is stacked above the rules-style
  // sheets, so the policy would open underneath it.
  const openPrivacyFromSettings = useCallback(() => {
    setShowSettings(false);
    setShowPrivacy(true);
  }, []);

  /**
   * A genuinely new player is shown the game rather than a menu. What counts as
   * "new" is in shouldOfferTutorial, where it is tested. `replace` so the back
   * gesture out of the tutorial can't land here and bounce them straight in again.
   */
  useEffect(() => {
    const offer = shouldOfferTutorial({
      seen: hasSeenTutorial(),
      canResume,
      roundsPlayed: statTotal(profile, "roundsPlayed"),
    });
    if (offer) nav("/tutorial", { replace: true, instant: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="screen home-screen">
      {/* The free corner: the home screen has no header to put it in. In the
          page's flow like the badge, so it slides with the screen. */}
      <FeedbackButton variant="corner" />
      <div style={{ textAlign: "center", marginTop: 20 }}>
        <div className="eyebrow">Higher &middot; Lower &middot; Match</div>
        <CardTitle />
        <div className="subtitle" style={{ marginTop: 6 }}>
          First to empty your deck wins.
        </div>
      </div>

      {/* Above the hand, not between it and the menu: on a short screen the
          hand tucks behind the menu card, and a panel in between would undo it. */}
      {notice && (
        <div className="card-panel col">
          <div className="subtitle">{notice}</div>
        </div>
      )}

      <HomeHand />

      <div className="card-panel col">
        {/* A round interrupted by a browser refresh is fully resumable, deck
            order and all, so picking it back up is the first thing offered. */}
        {canResume && (
          <button
            className="menu-btn primary"
            onClick={() => {
              if (resumeSavedRound()) nav("/local/play");
            }}
          >
            <MenuIcon name="resume" />
            Resume Round
          </button>
        )}
        {/* One tap to a solo table, dealt from the Quick Play table in
            Settings rather than from Play Locally's: that one is rebuilt for
            whoever is in the room, this one is a standing preference. A fresh
            match every time, like a deal from the setup screen. */}
        {/* The campaign shares Quick Play's row: both are a table for one, one
            dealt now and one a journey, and a row of its own would push the
            menu into a scroll on a small phone. */}
        <div className="menu-row">
          <button
            className={canResume ? "menu-btn" : "menu-btn primary"}
            onClick={() => {
              const quick = loadQuickPlay();
              startGame(toGameConfig(quickPlayTable(quick, accountStatus === "signedIn" ? username : null)));
              nav("/local/play", { hasTurnClock: quick.turnTimerSeconds != null });
            }}
          >
            <MenuIcon name="quick" />
            Quick Play
          </button>
          {/* Not in production yet - see campaign/release.ts. Quick Play
              takes the whole row without it, as it did before. */}
          {campaignVisible() && (
            <button className="menu-btn" onClick={() => nav("/campaign")}>
              <MenuIcon name="campaign" />
              Campaign
            </button>
          )}
        </div>
        <button className="menu-btn" onClick={() => nav("/local/setup")}>
          <MenuIcon name="local" />
          Play Locally
        </button>
        <button className="menu-btn" onClick={() => nav("/online")}>
          <MenuIcon name="online" />
          Play Online
        </button>
        {/* Settings rides beside Table & Cards rather than taking a row: it is
            two switches and a slider, and the room they set the mood for is the
            button next to it. */}
        <div className="menu-row">
          <button className="menu-btn" onClick={() => nav("/themes")}>
            <MenuIcon name="themes" />
            {/* The screen's own title: "Table & Card Themes" no longer fits a
                phone once the cog shares the row. */}
            Table &amp; Cards
          </button>
          <button className="menu-btn menu-btn-square" onClick={() => setShowSettings(true)} aria-label="Settings">
            <MenuIcon name="settings" />
          </button>
        </div>
        <button className="menu-btn" onClick={() => nav("/stats")}>
          <MenuIcon name="progress" />
          Progress &amp; Achievements
        </button>
        {/* Two ways to learn the game - the tutorials, in the order a new
            player meets what they teach, and the rules sheet - so the row that
            used to open the rules directly now opens into them. Inline rather
            than a screen of its own: a short list, and a route to hold it would
            cost a navigation each way to say nothing. */}
        <button
          className={`menu-btn${howToOpen ? " menu-btn-open" : ""}`}
          aria-expanded={howToOpen}
          onClick={() => setHowToOpen((open) => !open)}
        >
          <MenuIcon name="rules" />
          How to Play
          <span className="menu-btn-caret" aria-hidden="true">
            &#9662;
          </span>
        </button>
        {howToOpen && (
          <div className="menu-submenu">
            {TUTORIALS.map((t) => (
              <button key={t.id} className="menu-btn" onClick={() => nav(`/tutorial/${t.id}`)}>
                <MenuIcon name="tutorial" />
                {t.title}
              </button>
            ))}
            <button className="menu-btn" onClick={() => setShowRules(true)}>
              <MenuIcon name="rulebook" />
              Rules
            </button>
          </div>
        )}
      </div>

      {/* Who you are and how far you've got, in one panel: the level is this
          player's, so the name it belongs to sits on it. */}
      <div className="card-panel col" style={{ padding: 16 }}>
        <div className="row home-player-row">
          <div className="home-level-text">
            <span className="section-label">Level {progress.level}</span>
            <span className="subtitle">
              {progress.toNextLevel === null ? "max level" : `${progress.toNextLevel.toLocaleString()} XP to next`}
            </span>
          </div>
          {/* Nothing is shown until auth has reported in: the store starts on
              "loading", and an "Account" that turns into someone's name a moment
              later is worse than one that arrives a moment late. The pill keeps
              its space while hidden so the panel doesn't grow when it lands. */}
          <button
            className="home-account"
            style={accountStatus === "loading" ? { visibility: "hidden" } : undefined}
            onClick={() => nav("/account")}
          >
            {accountStatus === "signedIn" ? (username ?? "Set username") : "Account"}
          </button>
        </div>
        <div className="level-bar">
          <div className="level-bar-fill" style={{ width: `${progress.fraction * 100}%` }} />
        </div>
      </div>

      {showRules && <RulesModal deckConfig={deckConfig} reference onClose={() => setShowRules(false)} />}
      {showSettings && <SettingsModal onClose={closeSettings} onOpenPrivacy={openPrivacyFromSettings} />}
      {/* Not code-split like What's New: whether the link exists at all depends
          on the policy's Status line, so the text is in this chunk regardless,
          and splitting off only the renderer would save next to nothing. */}
      {showPrivacy && <PrivacyModal onClose={closePrivacy} />}
      {showWhatsNew && (
        // No fallback: the sheet is a few kB off a warm cache, and a spinner
        // flashing in front of it would be the only thing most people saw.
        <Suspense fallback={null}>
          <WhatsNewModal onClose={() => setShowWhatsNew(false)} />
        </Suspense>
      )}

      {/* Which build this is, and the way into the release notes. Home screen
          only - it is a support aid, not chrome the game should carry into play.
          At the foot of the page, in its flow: pinned to the corner it sat on
          top of the menu whenever the page was tall enough to scroll.

          It was a plain label until the changelog needed a door: the file is the
          only place that says what changed, and a player had no way to read it.
          The build string is still copyable for a bug report, inside the sheet
          this opens. */}
      <div className="home-foot">
        {/* The policy's door, beside the badge because that is where a player
            already goes for "about this app" things. Quieter than the badge,
            and absent in production while the policy is still a draft. Like
            the badge it is in the page's flow, not fixed chrome, so it needs no
            `data-tx-chrome`: it slides with the screen it belongs to. */}
        {privacyLinkVisible() && (
          <button className="privacy-link" onClick={() => setShowPrivacy(true)}>
            Privacy
          </button>
        )}
        <button
          className="version-badge"
          onClick={() => setShowWhatsNew(true)}
          aria-label={`Version ${versionLabel()} - see what's new`}
        >
          {versionLabel()}
        </button>
      </div>
    </div>
  );
}
